// ReportExportModal — the Reports tab's "Download PDF" dialog for the active
// view. The summary offers one toggle per summary block (all on by default);
// a focused view has no sections and states the range — and, on delivery, the
// record-type filter — it will capture. Generating queues the export, confirms
// it in a status line inside the dialog (a toast outside an aria-modal dialog
// is inert to assistive technology), and keeps the dialog open so the request
// can be watched through the report export history (pending → ready →
// download).
//
// The server composes the report when the request arrives, so the PDF is a
// capture of that moment. Each request carries a client-minted token: a retry
// after a refusal reuses it so the server recognises the repeat instead of
// starting a second export, and a success replaces it so a deliberate second
// export is a new one. The parent remounts the dialog per opening, so every
// opening starts with a fresh token and every section selected.

import { useEffect, useState } from "react";
import Modal from "../../common/Modal";
import RecentExportsList from "../contract-overview/RecentExportsList";
import { useRequestReportExport } from "../../../hooks/useContractExports";
import {
  REPORT_EXPORT_SECTION_KEYS,
  REPORT_EXPORT_SECTION_LABELS,
  type CreateReportExportInput,
  type DeliveryRecordType,
  type ReportExportSectionKey,
  type ReportExportSections,
  type ReportExportView,
  type ReportRange,
} from "../../../types/report";

// Summary blocks in the order the summary renders them.
const SECTION_ORDER: ReportExportSectionKey[] = [
  "band",
  "commentary",
  "essentials",
  "financial",
  "milestones",
  "deliverables",
  "kpis",
  "issues",
  "variations",
  "insurance",
];

const VIEW_LABEL: Record<ReportExportView, string> = {
  summary: "Summary",
  delivery: "Delivery performance",
  kpi: "KPI compliance",
  financial: "Financial performance",
};

const RANGE_LABEL: Record<ReportRange, string> = {
  "1m": "Last month",
  "3m": "Last 3 months",
  "6m": "Last 6 months",
  "12m": "Last 12 months",
  all: "Contract to date",
};

const RECORD_TYPE_LABEL: Partial<Record<DeliveryRecordType, string>> = {
  milestone: "Milestones only",
  deliverable: "Deliverables only",
};

function allSections(on: boolean): ReportExportSections {
  return Object.fromEntries(
    REPORT_EXPORT_SECTION_KEYS.map((key) => [key, on]),
  ) as ReportExportSections;
}

function scopeLine(
  view: ReportExportView,
  range: ReportRange,
  recordType: DeliveryRecordType,
): string {
  const parts = [VIEW_LABEL[view], RANGE_LABEL[range]];
  if (view === "delivery" && RECORD_TYPE_LABEL[recordType]) {
    parts.push(RECORD_TYPE_LABEL[recordType]!);
  }
  return parts.join(" · ");
}

export interface ReportExportModalProps {
  contractId: string;
  isOpen: boolean;
  onClose: () => void;
  view: ReportExportView;
  range: ReportRange;
  recordType: DeliveryRecordType;
}

export default function ReportExportModal({
  contractId,
  isOpen,
  onClose,
  view,
  range,
  recordType,
}: ReportExportModalProps) {
  const [sections, setSections] = useState<ReportExportSections>(() => allSections(true));
  const [requestToken, setRequestToken] = useState(() => crypto.randomUUID());
  const [queued, setQueued] = useState<string | null>(null);
  const requestExport = useRequestReportExport(contractId);

  const isSummary = view === "summary";
  const selectedCount = SECTION_ORDER.filter((key) => sections[key]).length;
  const allOn = selectedCount === SECTION_ORDER.length;
  const canGenerate = !requestExport.isPending && (!isSummary || selectedCount > 0);

  function toggle(key: ReportExportSectionKey) {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleGenerate() {
    const input: CreateReportExportInput = {
      kind: "report",
      requestToken,
      view,
      range,
      ...(isSummary ? { sections } : {}),
      ...(view === "delivery" ? { recordType } : {}),
    };
    try {
      await requestExport.mutateAsync(input);
    } catch {
      // The refusal renders from the mutation's error state below; the token
      // is kept so a retry is recognised as the same request.
      return;
    }
    setRequestToken(crypto.randomUUID());
    setQueued("Report queued · see status below");
  }

  useEffect(() => {
    if (!queued) return;
    const timer = setTimeout(() => setQueued(null), 4000);
    return () => clearTimeout(timer);
  }, [queued]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Download report PDF" maxWidth="sm:max-w-lg">
      <div className="space-y-6">
        <p className="text-body-sm text-slate-600 dark:text-slate-400">
          The PDF captures the report as it stands when you generate it.
        </p>

        {isSummary ? (
          <fieldset>
            {/* The legend must be the fieldset's first child to name the group.
                Floating it lets the toggle-all button share its line. */}
            <legend className="float-left mb-2 text-body-sm font-semibold text-slate-900 dark:text-slate-100">
              Sections
            </legend>
            <button
              type="button"
              onClick={() => setSections(allSections(!allOn))}
              className="float-right mt-px text-caption text-brand-600 hover:underline dark:text-brand-400"
            >
              {allOn ? "Deselect all" : "Select all"}
            </button>
            <div className="clear-both grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SECTION_ORDER.map((key) => (
                <label
                  key={key}
                  className="flex cursor-pointer items-center gap-2 text-body-sm text-slate-700 dark:text-slate-300"
                >
                  <input
                    type="checkbox"
                    checked={sections[key]}
                    onChange={() => toggle(key)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-500 dark:border-slate-600"
                  />
                  {REPORT_EXPORT_SECTION_LABELS[key]}
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
          <section>
            <h4 className="mb-1 text-body-sm font-semibold text-slate-900 dark:text-slate-100">
              Report
            </h4>
            <p className="text-body-sm text-slate-700 dark:text-slate-300">
              {scopeLine(view, range, recordType)}
            </p>
          </section>
        )}

        {requestExport.isError && (
          <p
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-body-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {requestExport.error.message}
          </p>
        )}

        <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
          {isSummary && (
            <p role="status" className="mb-3 text-caption text-slate-500 dark:text-slate-400">
              {selectedCount > 0
                ? `${selectedCount} of ${SECTION_ORDER.length} sections`
                : "Select at least one section"}
            </p>
          )}
          {/* Always mounted so the confirmation is announced when it appears. */}
          <p
            role="status"
            className={`text-caption text-slate-600 dark:text-slate-300 ${queued ? "mb-3" : ""}`}
          >
            {queued}
          </p>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="btn-primary px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {requestExport.isPending ? "Generating…" : "Generate PDF"}
            </button>
          </div>
        </div>

        <RecentExportsList contractId={contractId} isOpen={isOpen} kind="report" />
      </div>
    </Modal>
  );
}
